import * as Redux from 'redux';
import thunkMiddleware, { ThunkAction as BaseThunkAction } from 'redux-thunk';
import { composeWithDevTools } from 'redux-devtools-extension';

import * as bankingActions from 'common/state/banking/actions';
import * as configActions from 'common/state/config/actions';
import * as loginActions from 'common/state/auth/actions';
import * as themeActions from 'features/nav/globalState/actions';
import { BaconCacheActions } from 'common/components/bacon/state';

import login from 'common/state/auth/reducers';
import theme from 'features/nav/globalState/reducers';
import { configReducer as config } from 'common/state/config/reducers';
import { baconReducer as bacon } from 'common/components/bacon/state';
import { api } from './api';
import {
  clientsReducer as clients,
  walletsReducer as wallets,
} from 'common/state/banking/reducers';

// An example
// import banking from '@totus/features/banking/state'

const rootReducer = Redux.combineReducers({
  auth: login,
  clients,
  wallets,
  config,
  theme: theme,
  bacon: bacon,
});
/**
 * The whole shape of our application state
 */
export type RootState = ReturnType<typeof rootReducer>;

/**
 * Union of all supported application actions
 */
export type PlainActions =
  | loginActions.AuthActions
  | bankingActions.UserClientsActions
  | bankingActions.UserWalletsActions
  | configActions.ConfigActions
  | themeActions.ThemeActions
  | BaconCacheActions; // add other actions as union

interface ThunkExtra {
  /** Axios instance bound to the totus API. */
  api: typeof api;
}

/**
 * A thunk returned by a thunk-creator.
 */
export type ThunkAction<R> = BaseThunkAction<
  R | Promise<R>,
  RootState,
  ThunkExtra /* this is the extra argument again */,
  PlainActions
>;

let initialState: Partial<RootState> | undefined = undefined;

try {
  const serializedState = localStorage.getItem('redux-state');

  if (serializedState) {
    initialState = JSON.parse(serializedState);
  }
} catch (err) {
  console.error('Error while trying to hydrate state from localStorage');
}

/**
 * Redux store
 * Initialises state using local storage
 * Uses thunk middleware
 */
export const store = Redux.createStore(
  rootReducer,
  initialState,
  composeWithDevTools(
    Redux.applyMiddleware(thunkMiddleware.withExtraArgument({ api }))
  )
);

/**
 * Updates local storage when the state tree is changed
 */
store.subscribe(() => {
  const state = store.getState();
  localStorage.setItem(
    'redux-state',
    JSON.stringify({ theme: state.theme, bacon: state.bacon })
  );
});

export default store;
